import React, { useEffect, useState, useRef, useCallback } from 'react';
import {
    Box,
    Typography,
    Button,
    useTheme,
    alpha,
    IconButton,
    Dialog,
    DialogTitle,
    DialogContent,
    DialogActions,
    TextField,
    CircularProgress,
    MenuItem,
    Alert
} from '@mui/material';
import {
    Refresh as RefreshIcon,
    AddRoad as BlockageIcon,
    Directions as PathIcon,
    Warning as AlertIcon,
    Delete as DeleteIcon
} from '@mui/icons-material';
import internalTransportService from '../../services/internalTransportService';
import GlassPanel from '../common/GlassPanel';

const WarehouseTrafficMap = ({ warehouseId = '1' }) => {
    const theme = useTheme();
    const canvasRef = useRef(null);
    const [graph, setGraph] = useState(null);
    const [blockages, setBlockages] = useState([]);
    const [loading, setLoading] = useState(true);
    const [selectedPath, setSelectedPath] = useState(null);
    
    // Interaction State
    const [reportModalOpen, setReportModalOpen] = useState(false);
    const [pathModalOpen, setPathModalOpen] = useState(false);
    const [pathRequest, setPathRequest] = useState({ start: '', end: '' });

    // Blockage Form
    const [blockageReason, setBlockageReason] = useState('Spill');
    const [blockageDuration, setBlockageDuration] = useState(60);

    const fetchData = useCallback(async () => {
        setLoading(true);
        try {
            // Using Promise.allSettled because one might fail while wiring is in progress
            const results = await Promise.allSettled([
                internalTransportService.getWarehouseGraph(warehouseId),
                internalTransportService.getActiveBlockages(warehouseId)
            ]);
            
            if (results[0].status === 'fulfilled') {
                setGraph(results[0].value);
            }
            if (results[1].status === 'fulfilled') {
                setBlockages(results[1].value.active_blockages || []);
            }
        } catch (error) {
            console.error("Failed to fetch traffic data", error);
        } finally {
            setLoading(false);
        }
    }, [warehouseId]);

    useEffect(() => {
        fetchData();
    }, [fetchData]);

    // Drawing Logic
    useEffect(() => {
        if (!graph || !canvasRef.current) return;

        const canvas = canvasRef.current;
        const rect = canvas.getBoundingClientRect();
        // Set actual canvas size to match display size for sharpness
        canvas.width = rect.width;
        canvas.height = rect.height;

        const ctx = canvas.getContext('2d');
        const width = canvas.width;
        const height = canvas.height;

        // Clear
        ctx.clearRect(0, 0, width, height);

        // Scaling (Simple auto-scale)
        // Find bounds
        const xCoords = graph.nodes.map(n => n.x);
        const yCoords = graph.nodes.map(n => n.y);
        const minX = Math.min(...xCoords);
        const maxX = Math.max(...xCoords);
        const minY = Math.min(...yCoords);
        const maxY = Math.max(...yCoords);
        
        const padding = 50;
        const dataWidth = maxX - minX || 1;
        const dataHeight = maxY - minY || 1;
        
        const scaleX = (width - padding * 2) / dataWidth;
        const scaleY = (height - padding * 2) / dataHeight;
        const scale = Math.min(scaleX, scaleY);

        const project = (node) => ({
            x: (node.x - minX) * scale + padding + (width - dataWidth * scale - padding * 2) / 2, // Center X
            y: (node.y - minY) * scale + padding + (height - dataHeight * scale - padding * 2) / 2 // Center Y
        });

        // Draw Edges
        graph.edges.forEach(edge => {
            const source = graph.nodes.find(n => n.id === edge.source);
            const target = graph.nodes.find(n => n.id === edge.target);
            if (!source || !target) return;

            const p1 = project(source);
            const p2 = project(target);

            const isBlocked = blockages.some(b => 
                (b.source === edge.source && b.target === edge.target) || 
                (b.source === edge.target && b.target === edge.source) 
            );

            // Check if edge is part of the selected path
            let isPath = false;
            if (selectedPath && selectedPath.path) {
                for (let i = 0; i < selectedPath.path.length - 1; i++) {
                    const u = selectedPath.path[i];
                    const v = selectedPath.path[i+1];
                    if ((u === edge.source && v === edge.target) || (u === edge.target && v === edge.source)) {
                        isPath = true;
                        break;
                    }
                }
            }

            ctx.beginPath();
            ctx.moveTo(p1.x, p1.y);
            ctx.lineTo(p2.x, p2.y);
            
            ctx.lineWidth = isPath ? 6 : 4;
            ctx.lineCap = 'round';
            ctx.strokeStyle = isBlocked ? theme.palette.error.main : 
                              isPath ? theme.palette.success.main : 
                              alpha(theme.palette.text.primary, 0.1);
            
            if (isBlocked) {
                ctx.setLineDash([10, 5]);
            } else {
                ctx.setLineDash([]);
            }
            
            ctx.stroke();

            // Blockage Icon (Approximate center)
            if (isBlocked) {
                const midX = (p1.x + p2.x) / 2;
                const midY = (p1.y + p2.y) / 2;
                ctx.fillStyle = theme.palette.error.main;
                ctx.font = '24px Arial';
                ctx.textAlign = 'center';
                ctx.textBaseline = 'middle';
                ctx.fillText('⛔', midX, midY);
            }
            
            // Draw direction arrows if directed (simplified)
            if (edge.direction !== 'bidirectional') {
                 // Calculate angle
                 const angle = Math.atan2(p2.y - p1.y, p2.x - p1.x);
                 const midX = (p1.x + p2.x) / 2;
                 const midY = (p1.y + p2.y) / 2;
                 
                 ctx.save();
                 ctx.translate(midX, midY);
                 ctx.rotate(angle);
                 ctx.beginPath();
                 ctx.moveTo(-5, -5);
                 ctx.lineTo(5, 0);
                 ctx.lineTo(-5, 5);
                 ctx.fillStyle = alpha(theme.palette.text.primary, 0.3);
                 ctx.fill();
                 ctx.restore();
            }
        });

        // Draw Nodes
        graph.nodes.forEach(node => {
            const p = project(node);
            
            ctx.beginPath();
            ctx.arc(p.x, p.y, 8, 0, 2 * Math.PI);
            ctx.fillStyle = theme.palette.background.paper;
            ctx.fill();
            ctx.lineWidth = 2;
            ctx.strokeStyle = theme.palette.primary.main;
            ctx.stroke();

            // Label
            ctx.fillStyle = theme.palette.text.primary;
            ctx.font = 'bold 12px Inter';
            ctx.textAlign = 'center';
            ctx.fillText(node.id, p.x, p.y - 15);
        });

    }, [graph, blockages, selectedPath, theme]);

    const handleCalculatePath = async () => {
        try {
            const result = await internalTransportService.calculatePath(pathRequest.start, pathRequest.end, warehouseId);
            setSelectedPath(result);
            setPathModalOpen(false);
        } catch (e) {
            alert("Path calculation failed: " + e.message);
        }
    };

    const handleReportBlockage = async () => {
        if (!pathRequest.start || !pathRequest.end) return; 
        try {
            await internalTransportService.reportBlockage({
                source_node: pathRequest.start,
                target_node: pathRequest.end,
                reason: blockageReason,
                duration_minutes: blockageDuration
            });
            setReportModalOpen(false);
            fetchData();
        } catch (e) {
            alert("Failed to report blockage");
        }
    };

    return (
        <GlassPanel sx={{ height: '100%', display: 'flex', flexDirection: 'column', overflow: 'hidden' }}>
            <Box sx={{ p: 2, borderBottom: '1px solid', borderColor: 'divider', display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                <Box>
                    <Typography variant="h6" fontWeight="bold">Internal Traffic Control</Typography>
                    <Box sx={{ display: 'flex', gap: 2, alignItems: 'center' }}>
                        <Typography variant="caption" color="text.secondary">
                             Warehouse ID: {warehouseId}
                        </Typography>
                        <Typography variant="caption" sx={{ color: blockages.length > 0 ? 'error.main' : 'success.main', fontWeight: 'medium' }}>
                            • {blockages.length} Active Incidents
                        </Typography>
                    </Box>
                </Box>
                <Box sx={{ display: 'flex', gap: 1 }}>
                     <Button 
                        startIcon={<PathIcon />} 
                        variant="soft" 
                        size="small"
                        onClick={() => setPathModalOpen(true)}
                    >
                        Test AI Routing
                    </Button>
                    <Button 
                        startIcon={<BlockageIcon />} 
                        color="error"
                        variant="soft" 
                        size="small"
                        onClick={() => setReportModalOpen(true)}
                    >
                        Report Blockage
                    </Button>
                    <IconButton size="small" onClick={fetchData}>
                        <RefreshIcon />
                    </IconButton>
                </Box>
            </Box>

            <Box sx={{ flexGrow: 1, position: 'relative', bgcolor: alpha(theme.palette.background.default, 0.3) }}>
                {loading && (
                    <Box sx={{ position: 'absolute', inset: 0, display: 'flex', alignItems: 'center', justifyContent: 'center', zIndex: 10 }}>
                        <CircularProgress />
                    </Box>
                )}
                {!loading && !graph && (
                    <Box sx={{ p: 4, textAlign: 'center', mt: 10 }}>
                        <Alert severity="info" sx={{ display: 'inline-flex' }}>
                            No Graph Data Available. Ensure backend tms_service/internal_transport_router is running.
                        </Alert>
                    </Box>
                )}
                <canvas 
                    ref={canvasRef} 
                    style={{ width: '100%', height: '100%', display: 'block' }}
                />
            </Box>

            {/* Path Modal */}
            <Dialog open={pathModalOpen} onClose={() => setPathModalOpen(false)}>
                <DialogTitle>Test AI Pathfinding</DialogTitle>
                <DialogContent sx={{ minWidth: 400, pt: 2 }}>
                    <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2, mt:1 }}>
                        <TextField 
                            label="Start Node" 
                            value={pathRequest.start} 
                            onChange={(e) => setPathRequest({...pathRequest, start: e.target.value})}
                            select
                            fullWidth
                            size="small"
                        >
                             {graph?.nodes?.map(n => (
                                <MenuItem key={n.id} value={n.id}>{n.id} ({n.zone_id})</MenuItem>
                            ))}
                        </TextField>
                        <TextField 
                            label="End Node" 
                            value={pathRequest.end} 
                            onChange={(e) => setPathRequest({...pathRequest, end: e.target.value})}
                            select
                            fullWidth
                            size="small"
                        >
                            {graph?.nodes?.map(n => (
                                <MenuItem key={n.id} value={n.id}>{n.id} ({n.zone_id})</MenuItem>
                            ))}
                        </TextField>
                    </Box>
                </DialogContent>
                <DialogActions>
                    <Button onClick={() => setPathModalOpen(false)}>Cancel</Button>
                    <Button variant="contained" onClick={handleCalculatePath} disabled={!pathRequest.start || !pathRequest.end}>Calculate Path</Button>
                </DialogActions>
            </Dialog>

             {/* Blockage Modal */}
             <Dialog open={reportModalOpen} onClose={() => setReportModalOpen(false)}>
                <DialogTitle>Report Hazard / Blockage</DialogTitle>
                <DialogContent sx={{ minWidth: 400, pt: 2 }}>
                    <Alert severity="warning" sx={{ mb: 2 }}>
                        This will force the AI to re-route all AGV traffic immediately.
                    </Alert>
                    <Typography variant="subtitle2" gutterBottom>Select Affected Segment</Typography>
                    <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
                         <Box sx={{ display: 'flex', gap: 1 }}>
                            <TextField 
                                label="From Node" 
                                value={pathRequest.start} 
                                onChange={(e) => setPathRequest({...pathRequest, start: e.target.value})}
                                select
                                fullWidth
                                size="small"
                            >
                                {graph?.nodes?.map(n => (
                                    <MenuItem key={n.id} value={n.id}>{n.id}</MenuItem>
                                ))}
                            </TextField>
                            <TextField 
                                label="To Node" 
                                value={pathRequest.end} 
                                onChange={(e) => setPathRequest({...pathRequest, end: e.target.value})}
                                select
                                fullWidth
                                size="small"
                            >
                                {graph?.nodes?.map(n => (
                                    <MenuItem key={n.id} value={n.id}>{n.id}</MenuItem>
                                ))}
                            </TextField>
                         </Box>

                         <TextField 
                            label="Hazard Reason" 
                            value={blockageReason} 
                            onChange={(e) => setBlockageReason(e.target.value)}
                            fullWidth
                            size="small"
                            placeholder="e.g. Oil Spill, Pallet Obstruction"
                         />
                          <TextField 
                            label="Est. Duration (minutes)" 
                            type="number"
                            value={blockageDuration} 
                            onChange={(e) => setBlockageDuration(e.target.value)}
                            fullWidth
                            size="small"
                         />
                    </Box>
                </DialogContent>
                <DialogActions>
                    <Button onClick={() => setReportModalOpen(false)}>Cancel</Button>
                    <Button variant="contained" color="error" onClick={handleReportBlockage}>Confirm Blockage</Button>
                </DialogActions>
            </Dialog>
        </GlassPanel>
    );
};

export default React.memo(WarehouseTrafficMap);
